import React, {useEffect, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import {useHistory} from 'react-router-dom';
import Select from 'react-select';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faCalendarPlus, faCodeMerge, faEnvelope, faGlobe, faLocationDot, faPen, faPhone, faTimes, faTrashAlt
} from '@fortawesome/free-solid-svg-icons';
import API from '../services/API';
import Utils from '../services/Utils';
import {usePlan} from '../services/PlanContext';
import {findLanguage} from '../services/languages';
import {
  avatarColor, countryLabel, customerInitials, customerName, formatDate, formatMoney, formattedAddress
} from '../services/customers';

// Statut d'une réservation de l'historique : libellé + intention de couleur (mêmes règles que Réservations).
const bookingStatus = (booking) => {
  switch (booking.status) {
    case 'CONFIRMED': return {labelId: 'CONFIRMED', intent: 'success'};
    case 'VALIDATED': return {labelId: 'WAITING_CONFIRMATION', intent: 'warn'};
    case 'EXPIRED': return {labelId: 'EXPIRED', intent: 'danger'};
    case 'CANCELED': return {labelId: 'CANCELED', intent: 'muted'};
    default:
      if (booking.payment === 'FAILED') return {labelId: 'PAYMENT_FAILED', intent: 'danger'};
      if (booking.payment === 'ABANDONED') return {labelId: 'PAYMENT_NOT_FINALIZED', intent: 'danger'};
      return {labelId: 'WAITING_FOR_PAYMENT', intent: 'warn'};
  }
};

/**
 * Fiche client en panneau latéral (et non en modale) : les fenêtres ouvertes depuis la fiche (détail
 * d'une réservation, nouvelle réservation, modification, suppression) s'empilent par-dessus sans la
 * refermer, et le chauffeur la retrouve telle quelle en les fermant.
 */
export default ({
  intl, open, customerId, refreshKey, customers, onClose, onEdit, onDelete, onNewBooking, onOpenBooking, onMerged,
}) => {
  const history = useHistory();
  const {openSubscription} = usePlan();
  const [detail, setDetail] = useState(null);
  const [merging, setMerging] = useState(false);
  const [mergeSource, setMergeSource] = useState(null);

  useEffect(() => {
    if (!open || !customerId) return;
    setMerging(false);
    setMergeSource(null);
    API.getCustomer(history, customerId).then(response => {
      if (response && response.data.success === true) {
        setDetail(response.data.data);
      } else if (response && response.data.data === 'BOOKING_LOCKED') {
        onClose();
        openSubscription();
      } else {
        onClose();
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    });
  }, [open, customerId, refreshKey]);

  // Échap ferme la fiche, sauf si une fenêtre est ouverte par-dessus (elle gère sa propre fermeture).
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !document.body.classList.contains('modal-open')) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  // Fiche d'un autre client pas encore chargée : on n'affiche pas l'ancienne.
  const customer = detail && detail.customer && detail.customer.id === customerId ? detail.customer : null;
  const stats = customer ? customer.stats : null;
  const currency = detail ? detail.currency : 'EUR';
  const language = customer ? findLanguage(customer.language) : null;
  const address = formattedAddress(customer);
  const country = customer ? countryLabel(customer.country, intl.locale) : '';

  const mergeOptions = (customers || [])
    .filter(c => !c.locked && c.id !== customerId)
    .map(c => ({
      value: c.id,
      label: customerName(c) + ((c.email || c.phone) ? ` · ${c.email || c.phone}` : ''),
    }));

  const handleMerge = () => {
    if (!mergeSource) return;
    API.postMergeCustomers(history, mergeSource.value, customerId).then(response => {
      if (response && response.data.success === true) {
        Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'CUSTOMERS_MERGED'}));
        setMerging(false);
        setMergeSource(null);
        onMerged();
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    });
  };

  const Tile = ({labelId, value, accent}) => (
    <div className={`ccv2-cust-tile ${accent ? 'is-accent' : ''}`}>
      <div className="ccv2-cust-tile-value">{value}</div>
      <div className="ccv2-cust-tile-label"><FormattedMessage id={labelId}/></div>
    </div>
  );

  const InfoRow = ({labelId, children}) => (
    <div className="ccv2-cust-info-row">
      <span className="ccv2-cust-info-label"><FormattedMessage id={labelId}/></span>
      <span className="ccv2-cust-info-value">{children}</span>
    </div>
  );

  const dash = <span className="ccv2-cust-muted">-</span>;

  return (
    <>
      <div className={`ccv2-cust-overlay ${open ? 'is-open' : ''}`} onClick={onClose}/>
      <aside className={`ccv2-cust-panel ${open ? 'is-open' : ''}`} aria-hidden={!open}
             aria-label={intl.formatMessage({id: 'CUSTOMER_DETAILS'})}>
        <div className="ccv2-cust-panel-head">
          <button type="button" className="ccv2-cust-close" onClick={onClose} aria-label={intl.formatMessage({id: 'CLOSE'})}>
            <FontAwesomeIcon icon={faTimes}/>
          </button>
          {customer && (
            <>
              <div className="ccv2-cust-identity">
                <span className="ccv2-cust-avatar is-lg" style={{background: avatarColor(customer.id)}}>
                  {customerInitials(customer)}
                </span>
                <div className="ccv2-cust-identity-text">
                  <div className="ccv2-cust-panel-name">{customerName(customer)}</div>
                  {customer.companyName && <div className="ccv2-cust-panel-company">{customer.companyName}</div>}
                  <div className="ccv2-cust-chips">
                    <span className="ccv2-cust-chip">
                      <FormattedMessage id={customer.createdManually ? 'SOURCE_MANUAL' : 'SOURCE_MODULE'}/>
                    </span>
                    {stats.ridesCompleted >= 2 && (
                      <span className="ccv2-cust-chip is-accent"><FormattedMessage id="FILTER_REGULAR"/></span>
                    )}
                  </div>
                </div>
              </div>
              <div className="ccv2-cust-panel-actions">
                <button type="button" className="ccv2-btn ccv2-btn-primary" onClick={() => onNewBooking(customer)}>
                  <FontAwesomeIcon icon={faCalendarPlus}/> <FormattedMessage id="NEW_BOOKING"/>
                </button>
                <button type="button" className="ccv2-btn ccv2-btn-secondary" onClick={() => onEdit(customer)}>
                  <FontAwesomeIcon icon={faPen}/> <FormattedMessage id="EDIT"/>
                </button>
                {customer.phone && (
                  <a className="ccv2-btn ccv2-btn-secondary" href={`tel:${customer.phone}`}>
                    <FontAwesomeIcon icon={faPhone}/> <FormattedMessage id="CALL"/>
                  </a>
                )}
                {customer.email && (
                  <a className="ccv2-btn ccv2-btn-secondary" href={`mailto:${customer.email}`}>
                    <FontAwesomeIcon icon={faEnvelope}/> <FormattedMessage id="SEND_EMAIL"/>
                  </a>
                )}
              </div>
            </>
          )}
        </div>

        <div className="ccv2-cust-panel-body">
          {!customer && <div className="ccv2-cust-loading"><FormattedMessage id="LOADING"/></div>}
          {customer && (
            <>
              <div className="ccv2-cust-tiles">
                <Tile labelId="RIDES_COMPLETED" value={stats.ridesCompleted} accent/>
                <Tile labelId="RIDES_UPCOMING" value={stats.ridesUpcoming}/>
                <Tile labelId="TOTAL_SPENT" value={formatMoney(stats.totalSpent, currency)}/>
                <Tile labelId="AVERAGE_BASKET"
                      value={stats.ridesCompleted > 0 ? formatMoney(stats.totalSpent / stats.ridesCompleted, currency) : '-'}/>
              </div>

              <section className="ccv2-cust-section">
                <InfoRow labelId="LAST_RIDE">{formatDate(stats.lastRideAt, intl.locale) || dash}</InfoRow>
                <InfoRow labelId="NEXT_RIDE">{formatDate(stats.nextRideAt, intl.locale) || dash}</InfoRow>
                <InfoRow labelId="BOOKINGS_TOTAL">{stats.bookingsCount}</InfoRow>
                <InfoRow labelId="RIDES_CANCELED">{stats.ridesCanceled}</InfoRow>
                <InfoRow labelId="CUSTOMER_SINCE">{formatDate(customer.createdAt, intl.locale) || dash}</InfoRow>
              </section>

              <section className="ccv2-cust-section">
                <h3 className="ccv2-cust-section-title"><FormattedMessage id="CONTACT_INFO"/></h3>
                <div className="ccv2-cust-contact">
                  <div><FontAwesomeIcon icon={faEnvelope} fixedWidth/>
                    {customer.email ? <a href={`mailto:${customer.email}`}>{customer.email}</a> : dash}</div>
                  <div><FontAwesomeIcon icon={faPhone} fixedWidth/>
                    {customer.phone ? <a href={`tel:${customer.phone}`}>{customer.phone}</a> : dash}</div>
                  <div><FontAwesomeIcon icon={faLocationDot} fixedWidth/>
                    <span>{address || country ? [address, country].filter(Boolean).join(', ') : dash}</span></div>
                  <div><FontAwesomeIcon icon={faGlobe} fixedWidth/>
                    <span>{language.flag} {language.label}</span></div>
                </div>
              </section>

              <section className="ccv2-cust-section">
                <h3 className="ccv2-cust-section-title"><FormattedMessage id="NOTES"/></h3>
                {customer.notes
                  ? <div className="ccv2-cust-notes">{customer.notes}</div>
                  : (
                    <button type="button" className="ccv2-cust-link" onClick={() => onEdit(customer)}>
                      <FormattedMessage id="ADD_NOTE"/>
                    </button>
                  )}
              </section>

              <section className="ccv2-cust-section">
                <h3 className="ccv2-cust-section-title">
                  <FormattedMessage id="BOOKING_HISTORY"/>
                  <span className="ccv2-cust-count">{detail.bookings.length}</span>
                </h3>
                {detail.bookings.length === 0 && (
                  <div className="ccv2-cust-muted"><FormattedMessage id="NO_BOOKING_YET"/></div>
                )}
                <div className="ccv2-cust-history">
                  {detail.bookings.map(booking => {
                    const status = bookingStatus(booking);
                    return (
                      <button key={booking.id} type="button" className="ccv2-cust-hist-row"
                              onClick={() => onOpenBooking(booking, detail.lengthUnit === 'MILE' ? 'MI' : 'KM')}>
                        <div className="ccv2-cust-hist-top">
                          <span className="ccv2-cust-hist-date">{booking.pickUpTime}</span>
                          <span className="ccv2-cust-hist-number">{Utils.formatBookingNumber(booking)}</span>
                          <span className="ccv2-cust-hist-price">
                            {formatMoney(booking.discount ? booking.discount.discountPrice : booking.price, booking.currency)}
                          </span>
                        </div>
                        <div className="ccv2-cust-hist-route">
                          {booking.startingAddress} <span className="ccv2-cust-muted">→</span> {booking.arrivalAddress}
                        </div>
                        <div className="ccv2-cust-hist-bottom">
                          <span className={`ccv2-cust-status is-${status.intent}`}>
                            <FormattedMessage id={status.labelId}/>
                          </span>
                          {booking.returnPickUpTime && (
                            <span className="ccv2-cust-muted"><FormattedMessage id="ROUND_TRIP"/></span>
                          )}
                          <span className="ccv2-cust-muted">{booking.vehicle}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>

              <section className="ccv2-cust-section ccv2-cust-danger-zone">
                {!merging ? (
                  <div className="ccv2-cust-panel-actions">
                    <button type="button" className="ccv2-btn ccv2-btn-secondary" onClick={() => setMerging(true)}
                            disabled={mergeOptions.length === 0}>
                      <FontAwesomeIcon icon={faCodeMerge}/> <FormattedMessage id="MERGE_DUPLICATE"/>
                    </button>
                    <button type="button" className="ccv2-btn ccv2-btn-danger" onClick={() => onDelete(customer)}>
                      <FontAwesomeIcon icon={faTrashAlt}/> <FormattedMessage id="DELETE_CUSTOMER"/>
                    </button>
                  </div>
                ) : (
                  <div className="ccv2-cust-merge">
                    <h3 className="ccv2-cust-section-title"><FormattedMessage id="MERGE_DUPLICATE"/></h3>
                    <p><FormattedMessage id="MERGE_DUPLICATE_DESC" values={{name: <b>{customerName(customer)}</b>}}/></p>
                    <Select options={mergeOptions} value={mergeSource} onChange={setMergeSource}
                            placeholder={intl.formatMessage({id: 'SELECT_CUSTOMER'})}
                            noOptionsMessage={() => intl.formatMessage({id: 'NO_CUSTOMER_MATCH'})}
                            menuPlacement="auto" styles={Utils.getSearchableSelectStyle()}/>
                    <div className="ccv2-cust-panel-actions mt-3">
                      <button type="button" className="ccv2-btn ccv2-btn-primary" disabled={!mergeSource} onClick={handleMerge}>
                        <FontAwesomeIcon icon={faCodeMerge}/> <FormattedMessage id="MERGE"/>
                      </button>
                      <button type="button" className="ccv2-btn ccv2-btn-secondary"
                              onClick={() => { setMerging(false); setMergeSource(null); }}>
                        <FormattedMessage id="CANCEL"/>
                      </button>
                    </div>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </aside>
    </>
  );
};
